import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Notification, ModalState } from '@/types/common';
import { getSystemTheme } from '@/theme';

interface UiState {
  theme: 'light' | 'dark' | 'system';
  notifications: Notification[];
  modals: ModalState;
}

const initialState: UiState = {
  theme: 'system', // Default to system preference
  notifications: [],
  modals: {
    valveControl: false,
    scheduleEditor: false,
    groupManager: false,
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleTheme: (state) => {
      // Cycle through themes: light -> dark -> system -> light
      if (state.theme === 'light') {
        state.theme = 'dark';
      } else if (state.theme === 'dark') {
        state.theme = 'system';
      } else {
        state.theme = 'light';
      }
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload;
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: `notification_${Date.now()}_${Math.random()}`,
        read: false,
      };
      state.notifications.unshift(notification);
      
      // Keep only last 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(
        notification => notification.id === action.payload
      );
      if (notification) {
        notification.read = true;
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.read = true;
      });
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
    },
    openModal: (state, action: PayloadAction<keyof ModalState>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof ModalState>) => {
      state.modals[action.payload] = false;
    },
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach(key => {
        state.modals[key as keyof ModalState] = false;
      });
    },
  },
});

// Selectors
export const selectTheme = (state: { ui: UiState }) => state.ui.theme;
export const selectResolvedTheme = (state: { ui: UiState }) => {
  const { theme } = state.ui;
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

export const {
  toggleTheme,
  setTheme,
  addNotification,
  removeNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  clearAllNotifications,
  openModal,
  closeModal,
  closeAllModals,
} = uiSlice.actions;

export default uiSlice.reducer;